/**
 * Taken from Radash version 12.1.0 (2024-03-08)
 * https://github.com/sodiray/radash/releases/tag/v12.1.0
 */

type Falsy = null | undefined | false | '' | 0 | 0n;
type Cache<T> = Record<string, { exp: number | null; value: T }>;
type DebounceFunction<TArgs extends any[]> = {
  (...args: TArgs): void;
  cancel(): void;
  isPending(): boolean;
  flush(...args: TArgs): void;
};

/**
 * Dynamically get a nested value from an array or
 * object with a string.
 *
 * @example get(person, 'friends[0].name')
 */
export const get = <TDefault = unknown>(value: any, path: string, defaultValue?: TDefault): TDefault => {
  const segments = path.split(/[.[\]]/g);
  let current: any = value;
  for (const key of segments) {
    if (current === null) return defaultValue as TDefault;
    if (current === undefined) return defaultValue as TDefault;
    const dequoted = key.replace(/['"]/g, '');
    if (dequoted.trim() === '') continue;
    current = current[dequoted];
  }
  if (current === undefined) return defaultValue as TDefault;
  return current;
};

/**
 * Debounce a function, so that it is only called after a certain amount of time has passed since the last call.
 * If it is called at a high frequency, it will only execute the function once after the calls have stopped for the specified delay.
 * The last call is guaranteed to be executed.
 */
export const debounce = <TArgs extends any[]>({ delay }: { delay: number }, func: (...args: TArgs) => any) => {
  let timer: NodeJS.Timeout | undefined;
  let isActive = true;

  const debounced: DebounceFunction<TArgs> = (...args: TArgs) => {
    if (isActive) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isActive && func(...args);
        timer = undefined;
      }, delay);
    } else {
      func(...args);
    }
  };
  debounced.isPending = () => {
    return timer !== undefined;
  };
  debounced.cancel = () => {
    clearTimeout(timer);
    timer = undefined;
    isActive = false;
  };
  debounced.flush = (...args: TArgs) => func(...args);

  return debounced;
};

export const isDate = (value: any): value is Date => {
  return Object.prototype.toString.call(value) === '[object Date]';
};

export const isFunction = (value: any) => {
  return !!(value?.constructor && value.call && value.apply);
};

export const isNumber = (value: any): value is number => {
  try {
    return Number(value) === value;
  } catch {
    return false;
  }
};

export const isSymbol = (value: any): value is symbol => {
  return !!value && value.constructor === Symbol;
};

export const isEmpty = (value: any) => {
  if (value === true || value === false) return true;
  if (value === null || value === undefined) return true;
  if (isNumber(value)) return value === 0;
  if (isDate(value)) return Number.isNaN(value.getTime());
  if (isFunction(value)) return false;
  if (isSymbol(value)) return false;
  const length = value.length;
  if (isNumber(length)) return length === 0;
  const size = value.size;
  if (isNumber(size)) return size === 0;
  const keys = Object.keys(value).length;
  return keys === 0;
};

/**
 * Creates a memoized function. The returned function
 * will only execute the source function when no value
 * has previously been computed. If a ttl (milliseconds)
 * is given previously computed values will be checked
 * for expiration before being returned.
 */
export const memo = <TArgs extends any[], TResult>(
  func: (...args: TArgs) => TResult,
  options: {
    key?: (...args: TArgs) => string;
    ttl?: number;
  } = {},
) => {
  return memoize({}, func, options.key ?? null, options.ttl ?? null) as (...args: TArgs) => TResult;
};

const memoize = <TArgs extends any[], TResult>(
  cache: Cache<TResult>,
  func: (...args: TArgs) => TResult,
  keyFunc: ((...args: TArgs) => string) | null,
  ttl: number | null,
) => {
  return function callWithMemo(...args: any): TResult {
    const key = keyFunc ? keyFunc(...args) : JSON.stringify({ args });
    const existing = cache[key];
    if (existing !== undefined) {
      if (!existing.exp) return existing.value;
      if (existing.exp > Date.now()) {
        return existing.value;
      }
    }
    const result = func(...args);
    cache[key] = {
      exp: ttl ? Date.now() + ttl : null,
      value: result,
    };
    return result;
  };
};

/**
 * Given a list returns a new list with
 * only truthy values
 */
export const sift = <T>(list: readonly (T | Falsy)[]): T[] => {
  return (list?.filter((x) => !!x) as T[]) ?? [];
};
